import React, { useState, useEffect } from 'react';
import './MapDesigner.css';
import {
  saveMapStyleFromMapToStorage
} from '../../utils/mapStylePersistence';

// Default colors from the map style
// Landuse keys match Tegola layer names so each segregation can be colored separately
const DEFAULT_COLORS = {
  background: '#FFFFFF',
  water: '#8BD8EA',
  landuse_vegetation: '#BBECCF',
  landuse_agriculture_grass: '#BBECCF',
  landuse_barren_wetland: '#BBECCF',
  greenery_leisure: '#A2E0BA',
  buildings: '#e0d9ce',
  motorway: '#fc8',
  motorway_casing: null,
  primary: '#fea',
  primary_casing: null,
  tertiary: '#CCD8E2',
  tertiary_casing: null,
  residential: '#fff',
  residential_casing: null,
  service: '#CCD8E2',
  service_casing: null,
  railways: '#888',
  aeroways: '#c0b8d0',
  boundaries: '#555555',
  paths: '#ccc',
  paths_casing: null,
  labels: '#333',
};

// Road categories that have center + casing layers and support zoom-based colors
const ROAD_CATEGORIES = ['motorway', 'primary', 'tertiary', 'residential', 'service', 'paths'];

// Zoom breakpoints for road color interpolation (5 bands: 6–10, 10–14, 14–18, 18–22)
const ZOOM_STOPS = [6, 10, 14, 18, 22];
const ZOOM_KEYS = ['z6', 'z10', 'z14', 'z18', 'z22'];
const ZOOM_LABELS = [
  { key: 'z6', label: 'Zoom 6–10' },
  { key: 'z10', label: 'Zoom 10–14' },
  { key: 'z14', label: 'Zoom 14–18' },
  { key: 'z18', label: 'Zoom 18–22' },
  { key: 'z22', label: 'Zoom 22+' },
];

// Expand shorthand hex to 6-char (e.g. #fc8 -> #ffcc88)
const expandHex = (color) => {
  if (!color || typeof color !== 'string' || !color.startsWith('#')) return color;
  if (color.length === 4) {
    return '#' + color[1] + color[1] + color[2] + color[2] + color[3] + color[3];
  }
  return color.length === 7 ? color : null;
};

// Extract a single hex color from MapLibre paint value (solid or expression)
// interpolate: ["interpolate", "linear", ["zoom"], z1, color1, z2, color2] -> colors at odd indices
// step: ["step", ["zoom"], defaultColor, stop1, color1, ...] -> colors at even indices (0, 2, 4...)
const normalizeColor = (color) => {
  if (!color) return null;
  if (typeof color === 'string' && color.startsWith('#')) {
    return expandHex(color);
  }
  if (!Array.isArray(color) || color.length < 2) return null;
  if (color[0] === 'interpolate') {
    const stops = color.slice(3);
    for (let i = 1; i < stops.length; i += 2) {
      if (typeof stops[i] === 'string' && stops[i].startsWith('#')) {
        return expandHex(stops[i]);
      }
    }
  }
  if (color[0] === 'step') {
    const stops = color.slice(2); // [defaultColor, stop1, color1, stop2, color2, ...]
    for (let i = 0; i < stops.length; i += 2) {
      if (typeof stops[i] === 'string' && stops[i].startsWith('#')) {
        return expandHex(stops[i]);
      }
    }
  }
  return null;
};

const MapDesigner = ({ mapRef, onClose }) => {
  const [activeTab, setActiveTab] = useState('background');
  const [isApplying, setIsApplying] = useState(false);
  
  // State for colors - will be initialized from map
  const [colors, setColors] = useState(DEFAULT_COLORS);

  // Zoom-based road colors: { motorway: { z6, z10, z14, z18, z22 }, ... }
  const [zoomColors, setZoomColors] = useState(() => {
    const initial = {};
    ROAD_CATEGORIES.forEach(id => {
      const def = DEFAULT_COLORS[id] || '#ccc';
      initial[id] = ZOOM_KEYS.reduce((acc, k) => ({ ...acc, [k]: def }), {});
    });
    return initial;
  });
  // Zoom-based border (casing) colors when useZoomColors is on
  const [zoomCasingColors, setZoomCasingColors] = useState(() => {
    const initial = {};
    ROAD_CATEGORIES.forEach(id => {
      initial[id] = ZOOM_KEYS.reduce((acc, k) => ({ ...acc, [k]: '#999999' }), {});
    });
    return initial;
  });
  const [useZoomColors, setUseZoomColors] = useState(() => {
    const initial = {};
    ROAD_CATEGORIES.forEach(id => { initial[id] = false; });
    return initial;
  });

  // Store original colors for reset
  const [originalColors] = useState(DEFAULT_COLORS);

  // Fill the tool with current map colors when the designer opens (after map/style is ready)
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    const readColorsFromMap = () => {
      try {
        const currentColors = { ...DEFAULT_COLORS };

        if (map.getLayer('background')) {
          const normalized = normalizeColor(map.getPaintProperty('background', 'background-color'));
          if (normalized) currentColors.background = normalized;
        }
        if (map.getLayer('water_lakes')) {
          const normalized = normalizeColor(map.getPaintProperty('water_lakes', 'fill-color'));
          if (normalized) currentColors.water = normalized;
        }
        if (map.getLayer('landuse_vegetation')) {
          const c = normalizeColor(map.getPaintProperty('landuse_vegetation', 'fill-color'));
          if (c) currentColors.landuse_vegetation = c;
        }
        if (map.getLayer('landuse_agriculture_grass')) {
          const c = normalizeColor(map.getPaintProperty('landuse_agriculture_grass', 'fill-color'));
          if (c) currentColors.landuse_agriculture_grass = c;
        }
        if (map.getLayer('landuse_barren_wetland')) {
          const c = normalizeColor(map.getPaintProperty('landuse_barren_wetland', 'fill-color'));
          if (c) currentColors.landuse_barren_wetland = c;
        }
        if (map.getLayer('greenery_leisure')) {
          const c = normalizeColor(map.getPaintProperty('greenery_leisure', 'fill-color'));
          if (c) currentColors.greenery_leisure = c;
        }
        if (map.getLayer('buildings')) {
          const normalized = normalizeColor(map.getPaintProperty('buildings', 'fill-color'));
          if (normalized) currentColors.buildings = normalized;
        }
        if (map.getLayer('roads_mw_trunk_mid')) {
          const normalized = normalizeColor(map.getPaintProperty('roads_mw_trunk_mid', 'line-color'));
          if (normalized) currentColors.motorway = normalized;
        }
        if (map.getLayer('roads_mw_trunk_mid_casing')) {
          const c = normalizeColor(map.getPaintProperty('roads_mw_trunk_mid_casing', 'line-color'));
          if (c) currentColors.motorway_casing = c;
        }
        if (map.getLayer('roads_primary')) {
          const normalized = normalizeColor(map.getPaintProperty('roads_primary', 'line-color'));
          if (normalized) currentColors.primary = normalized;
        }
        if (map.getLayer('roads_primary_casing')) {
          const c = normalizeColor(map.getPaintProperty('roads_primary_casing', 'line-color'));
          if (c) currentColors.primary_casing = c;
        }
        if (map.getLayer('roads_tertiary_mid')) {
          const normalized = normalizeColor(map.getPaintProperty('roads_tertiary_mid', 'line-color'));
          if (normalized) currentColors.tertiary = normalized;
        }
        if (map.getLayer('roads_tertiary_mid_casing')) {
          const c = normalizeColor(map.getPaintProperty('roads_tertiary_mid_casing', 'line-color'));
          if (c) currentColors.tertiary_casing = c;
        }
        if (map.getLayer('roads_residential_high')) {
          const normalized = normalizeColor(map.getPaintProperty('roads_residential_high', 'line-color'));
          if (normalized) currentColors.residential = normalized;
        }
        if (map.getLayer('roads_residential_high_casing')) {
          const c = normalizeColor(map.getPaintProperty('roads_residential_high_casing', 'line-color'));
          if (c) currentColors.residential_casing = c;
        }
        if (map.getLayer('roads_service_high')) {
          const normalized = normalizeColor(map.getPaintProperty('roads_service_high', 'line-color'));
          if (normalized) currentColors.service = normalized;
        }
        if (map.getLayer('roads_service_high_casing')) {
          const c = normalizeColor(map.getPaintProperty('roads_service_high_casing', 'line-color'));
          if (c) currentColors.service_casing = c;
        }
        if (map.getLayer('railways_main')) {
          const normalized = normalizeColor(map.getPaintProperty('railways_main', 'line-color'));
          if (normalized) currentColors.railways = normalized;
        }
        if (map.getLayer('aeroways_runways_taxiways')) {
          const normalized = normalizeColor(map.getPaintProperty('aeroways_runways_taxiways', 'line-color'));
          if (normalized) currentColors.aeroways = normalized;
        }
        if (map.getLayer('admin_l2_4_country')) {
          const normalized = normalizeColor(map.getPaintProperty('admin_l2_4_country', 'line-color'));
          if (normalized) currentColors.boundaries = normalized;
        }
        if (map.getLayer('roads_paths')) {
          const normalized = normalizeColor(map.getPaintProperty('roads_paths', 'line-color'));
          if (normalized) currentColors.paths = normalized;
        }
        if (map.getLayer('roads_paths_casing')) {
          const c = normalizeColor(map.getPaintProperty('roads_paths_casing', 'line-color'));
          if (c) currentColors.paths_casing = c;
        }
        if (map.getLayer('places_city_town_combined')) {
          const normalized = normalizeColor(map.getPaintProperty('places_city_town_combined', 'text-color'));
          if (normalized) currentColors.labels = normalized;
        }

        setColors(currentColors);
      } catch (err) {
        // Keep defaults if read fails
      }
    };

    if (map.isStyleLoaded && map.isStyleLoaded()) {
      readColorsFromMap();
    } else {
      map.once('style.load', readColorsFromMap);
    }

    return () => {
      map.off('style.load', readColorsFromMap);
    };
  }, [mapRef]);

  const colorCategories = [
    { id: 'background', label: 'Background', description: 'Base map color' },
    { id: 'water', label: 'Water', description: 'Lakes, rivers, streams' },
    { id: 'landuse_vegetation', label: 'Vegetation', description: 'Forests, woods, plantations (Tegola: landuse_vegetation)' },
    { id: 'landuse_agriculture_grass', label: 'Agriculture & Grass', description: 'Farmland, grass (Tegola: landuse_agriculture_grass)' },
    { id: 'landuse_barren_wetland', label: 'Barren & Wetland', description: 'Barren land, wetlands (Tegola: landuse_barren_wetland)' },
    { id: 'greenery_leisure', label: 'Greenery & Leisure', description: 'Parks, gardens, leisure (Tegola: greenery_leisure)' },
    { id: 'buildings', label: 'Buildings', description: 'Building fills' },
    { id: 'motorway', label: 'Motorways', description: 'Major highways, trunk' },
    { id: 'primary', label: 'Primary & Secondary', description: 'Primary and secondary roads' },
    { id: 'tertiary', label: 'Tertiary', description: 'Tertiary roads' },
    { id: 'residential', label: 'Residential', description: 'Residential streets' },
    { id: 'service', label: 'Service', description: 'Service roads, access' },
    { id: 'paths', label: 'Paths', description: 'Walking paths, trails' },
    { id: 'railways', label: 'Railways', description: 'Train tracks' },
    { id: 'aeroways', label: 'Aeroways', description: 'Runways, taxiways' },
    { id: 'boundaries', label: 'Boundaries', description: 'Country, state borders' },
    { id: 'labels', label: 'Labels', description: 'Place names, text' },
  ];

  const handleColorChange = (category, newColor) => {
    setColors(prev => ({ ...prev, [category]: newColor }));
  };

  const handleCasingChange = (category, newColor) => {
    setColors(prev => ({ ...prev, [category + '_casing']: newColor || null }));
  };

  const handleZoomColorChange = (category, level, newColor) => {
    setZoomColors(prev => ({
      ...prev,
      [category]: { ...prev[category], [level]: newColor }
    }));
  };

  const handleZoomCasingColorChange = (category, level, newColor) => {
    setZoomCasingColors(prev => ({
      ...prev,
      [category]: { ...prev[category], [level]: newColor }
    }));
  };

  const handleUseZoomChange = (category, checked) => {
    setUseZoomColors(prev => ({ ...prev, [category]: checked }));
  };

  // Apply only a single category's colors to the map (avoids water change affecting roads)
  const applyCategoryToMap = (categoryId) => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    if (categoryId === 'background') {
      if (map.getLayer('background')) {
        map.setPaintProperty('background', 'background-color', colors.background);
      }
      return;
    }

    if (categoryId === 'water') {
      ['water_lakes'].forEach(layerId => {
        if (map.getLayer(layerId)) {
          map.setPaintProperty(layerId, 'fill-color', colors.water);
        }
      });
      if (map.getLayer('water_streams_canals')) {
        map.setPaintProperty('water_streams_canals', 'line-color', colors.water);
      }
      return;
    }

    if (categoryId === 'landuse_vegetation') {
      if (map.getLayer('landuse_vegetation')) {
        map.setPaintProperty('landuse_vegetation', 'fill-color', colors.landuse_vegetation);
      }
      return;
    }
    if (categoryId === 'landuse_agriculture_grass') {
      if (map.getLayer('landuse_agriculture_grass')) {
        map.setPaintProperty('landuse_agriculture_grass', 'fill-color', colors.landuse_agriculture_grass);
      }
      return;
    }
    if (categoryId === 'landuse_barren_wetland') {
      if (map.getLayer('landuse_barren_wetland')) {
        map.setPaintProperty('landuse_barren_wetland', 'fill-color', colors.landuse_barren_wetland);
      }
      return;
    }
    if (categoryId === 'greenery_leisure') {
      if (map.getLayer('greenery_leisure')) {
        map.setPaintProperty('greenery_leisure', 'fill-color', colors.greenery_leisure);
      }
      return;
    }

    if (categoryId === 'buildings') {
      if (map.getLayer('buildings')) {
        map.setPaintProperty('buildings', 'fill-color', colors.buildings);
      }
      if (map.getLayer('buildings_outline')) {
        const darkerBuilding = adjustBrightness(colors.buildings, -20);
        map.setPaintProperty('buildings_outline', 'line-color', darkerBuilding);
      }
      return;
    }

    if (categoryId === 'motorway') {
      ['roads_mw_trunk_low', 'roads_mw_trunk_mid'].forEach(layerId => {
        if (map.getLayer(layerId)) map.setPaintProperty(layerId, 'line-color', roadColorValue('motorway'));
      });
      ['roads_mw_trunk_low_casing', 'roads_mw_trunk_mid_casing'].forEach(layerId => {
        if (map.getLayer(layerId)) map.setPaintProperty(layerId, 'line-color', roadCasingColorValue('motorway'));
      });
      return;
    }
    if (categoryId === 'primary') {
      ['roads_primary', 'roads_secondary'].forEach(layerId => {
        if (map.getLayer(layerId)) map.setPaintProperty(layerId, 'line-color', roadColorValue('primary'));
      });
      ['roads_primary_casing', 'roads_secondary_casing'].forEach(layerId => {
        if (map.getLayer(layerId)) map.setPaintProperty(layerId, 'line-color', roadCasingColorValue('primary'));
      });
      return;
    }
    if (categoryId === 'tertiary') {
      if (map.getLayer('roads_tertiary_mid')) map.setPaintProperty('roads_tertiary_mid', 'line-color', roadColorValue('tertiary'));
      if (map.getLayer('roads_tertiary_mid_casing')) map.setPaintProperty('roads_tertiary_mid_casing', 'line-color', roadCasingColorValue('tertiary'));
      return;
    }
    if (categoryId === 'residential') {
      if (map.getLayer('roads_residential_high')) map.setPaintProperty('roads_residential_high', 'line-color', roadColorValue('residential'));
      if (map.getLayer('roads_residential_high_casing')) map.setPaintProperty('roads_residential_high_casing', 'line-color', roadCasingColorValue('residential'));
      return;
    }
    if (categoryId === 'service') {
      if (map.getLayer('roads_service_high')) map.setPaintProperty('roads_service_high', 'line-color', roadColorValue('service'));
      if (map.getLayer('roads_service_high_casing')) map.setPaintProperty('roads_service_high_casing', 'line-color', roadCasingColorValue('service'));
      return;
    }
    if (categoryId === 'paths') {
      if (map.getLayer('roads_paths')) map.setPaintProperty('roads_paths', 'line-color', roadColorValue('paths'));
      if (map.getLayer('roads_paths_casing')) map.setPaintProperty('roads_paths_casing', 'line-color', roadCasingColorValue('paths'));
      return;
    }

    if (categoryId === 'railways') {
      if (map.getLayer('railways_main')) map.setPaintProperty('railways_main', 'line-color', colors.railways);
      if (map.getLayer('railways_minor')) map.setPaintProperty('railways_minor', 'line-color', colors.railways);
      return;
    }
    if (categoryId === 'aeroways') {
      ['aeroways_runways_taxiways', 'aeroways_apron_terminal'].forEach(layerId => {
        if (map.getLayer(layerId)) {
          if (layerId === 'aeroways_apron_terminal') {
            map.setPaintProperty(layerId, 'fill-color', colors.aeroways);
          } else {
            map.setPaintProperty(layerId, 'line-color', colors.aeroways);
          }
        }
      });
      return;
    }
    if (categoryId === 'boundaries') {
      ['admin_l2_4_country', 'admin_l2_4_glow', 'admin_l2_4_states', 'admin_l5_8'].forEach(layerId => {
        if (map.getLayer(layerId)) {
          map.setPaintProperty(layerId, 'line-color', colors.boundaries);
        }
      });
      return;
    }
    if (categoryId === 'labels') {
      const labelLayers = [
        'places_country', 'places_country_fallback', 'places_country_state', 'places_country_state_fallback', 'places_city_town_combined',
        'places_village_suburb', 'water_lakes_labels',
        'landuse_vegetation_labels', 'landuse_agriculture_grass_labels',
        'buildings_labels', 'aeroways_apron_terminal_labels',
        'roads_mw_trunk_mid_labels', 'roads_primary_labels', 'roads_secondary_labels',
        'roads_tertiary_mid_labels', 'roads_residential_high_labels'
      ];
      labelLayers.forEach(layerId => {
        if (map.getLayer(layerId)) {
          map.setPaintProperty(layerId, 'text-color', colors.labels);
        }
      });
    }
  };

  const applyColors = (categoryIdOrAll) => {
    if (!mapRef.current) return;
    setIsApplying(true);

    try {
      const applyAll = categoryIdOrAll === undefined || categoryIdOrAll === 'all';
      if (applyAll) {
        // Apply every category in order
        colorCategories.forEach((cat) => applyCategoryToMap(cat.id));
      } else {
        applyCategoryToMap(categoryIdOrAll);
      }

      saveMapStyleFromMapToStorage(mapRef.current);

      setTimeout(() => {
        setIsApplying(false);
      }, 500);
    } catch (error) {
      alert('Failed to apply some colors. Please try again.');
      setIsApplying(false);
    }
  };

  // Helper function to adjust color brightness (accepts 4- or 6-char hex)
  const adjustBrightness = (hex, percent) => {
    const expanded = expandHex(hex);
    if (!expanded || expanded.length !== 7) return hex;
    hex = expanded.replace('#', '');
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return expanded;
    r = Math.max(0, Math.min(255, r + percent));
    g = Math.max(0, Math.min(255, g + percent));
    b = Math.max(0, Math.min(255, b + percent));
    return '#' + r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0');
  };

  // Build paint value: solid color or zoom interpolate expression (5 stops)
  const roadColorValue = (categoryId) => {
    if (useZoomColors[categoryId] && zoomColors[categoryId]) {
      const z = zoomColors[categoryId];
      const fallback = expandHex(colors[categoryId]) || '#cccccc';
      let prev = fallback;
      const values = ZOOM_KEYS.map((key) => {
        const v = expandHex(z[key]) || prev;
        if (v) prev = v;
        return v;
      });
      if (values.every(Boolean)) {
        const stops = ZOOM_STOPS.flatMap((stop, i) => [stop, values[i]]);
        return ['interpolate', ['linear'], ['zoom'], ...stops];
      }
    }
    const c = colors[categoryId];
    return expandHex(c) || c;
  };

  const roadCasingColorValue = (categoryId) => {
    if (useZoomColors[categoryId] && zoomCasingColors[categoryId]) {
      const z = zoomCasingColors[categoryId];
      const roadZ = zoomColors[categoryId] || {};
      const fallback = expandHex(colors[categoryId + '_casing']) || adjustBrightness(colors[categoryId] || '#ccc', -30);
      const values = ZOOM_KEYS.map((key) => {
        const casing = expandHex(z[key]);
        if (casing) return casing;
        const road = expandHex(roadZ[key]);
        return road ? adjustBrightness(road, -30) : fallback;
      });
      if (values.every(Boolean)) {
        const stops = ZOOM_STOPS.flatMap((stop, i) => [stop, values[i]]);
        return ['interpolate', ['linear'], ['zoom'], ...stops];
      }
    }
    const casing = colors[categoryId + '_casing'];
    if (casing && expandHex(casing)) return expandHex(casing);
    const center = colors[categoryId];
    return center ? adjustBrightness(center, -30) : '#999999';
  };

  const resetColors = () => {
    setColors(originalColors);
    // Also apply the original colors to the map
    if (!mapRef.current) return;
    
    const map = mapRef.current;
    
    try {
      // Reset background
      if (map.getLayer('background')) {
        map.setPaintProperty('background', 'background-color', originalColors.background);
      }

      // Reset water
      ['water_lakes'].forEach(layerId => {
        if (map.getLayer(layerId)) {
          map.setPaintProperty(layerId, 'fill-color', originalColors.water);
        }
      });
      
      if (map.getLayer('water_streams_canals')) {
        map.setPaintProperty('water_streams_canals', 'line-color', originalColors.water);
      }

      // Reset landuse/vegetation (solid defaults matching tegola-vector-config)
      if (map.getLayer('landuse_vegetation')) {
        map.setPaintProperty('landuse_vegetation', 'fill-color', originalColors.landuse_vegetation);
      }
      if (map.getLayer('landuse_agriculture_grass')) {
        map.setPaintProperty('landuse_agriculture_grass', 'fill-color', originalColors.landuse_agriculture_grass);
      }
      if (map.getLayer('landuse_barren_wetland')) {
        map.setPaintProperty('landuse_barren_wetland', 'fill-color', originalColors.landuse_barren_wetland);
      }
      if (map.getLayer('greenery_leisure')) {
        map.setPaintProperty('greenery_leisure', 'fill-color', originalColors.greenery_leisure);
      }

      // Reset buildings
      if (map.getLayer('buildings')) {
        map.setPaintProperty('buildings', 'fill-color', originalColors.buildings);
      }
      
      if (map.getLayer('buildings_outline')) {
        map.setPaintProperty('buildings_outline', 'line-color', '#c9c0b5');
      }

      // Reset motorways
      ['roads_mw_trunk_low', 'roads_mw_trunk_mid'].forEach(layerId => {
        if (map.getLayer(layerId)) map.setPaintProperty(layerId, 'line-color', originalColors.motorway);
      });
      ['roads_mw_trunk_low_casing', 'roads_mw_trunk_mid_casing'].forEach(layerId => {
        if (map.getLayer(layerId)) map.setPaintProperty(layerId, 'line-color', '#cad9eb');
      });

      // Reset primary/secondary
      ['roads_primary', 'roads_secondary'].forEach(layerId => {
        if (map.getLayer(layerId)) map.setPaintProperty(layerId, 'line-color', originalColors.primary);
      });
      ['roads_primary_casing', 'roads_secondary_casing'].forEach(layerId => {
        if (map.getLayer(layerId)) map.setPaintProperty(layerId, 'line-color', '#cad9eb');
      });

      // Reset tertiary
      if (map.getLayer('roads_tertiary_mid')) map.setPaintProperty('roads_tertiary_mid', 'line-color', originalColors.tertiary);
      if (map.getLayer('roads_tertiary_mid_casing')) map.setPaintProperty('roads_tertiary_mid_casing', 'line-color', '#e0e0e0');

      // Reset residential
      if (map.getLayer('roads_residential_high')) map.setPaintProperty('roads_residential_high', 'line-color', originalColors.residential);
      if (map.getLayer('roads_residential_high_casing')) map.setPaintProperty('roads_residential_high_casing', 'line-color', '#d0d0d0');

      // Reset service
      if (map.getLayer('roads_service_high')) map.setPaintProperty('roads_service_high', 'line-color', originalColors.service);
      if (map.getLayer('roads_service_high_casing')) map.setPaintProperty('roads_service_high_casing', 'line-color', '#d0d0d0');

      // Reset paths
      if (map.getLayer('roads_paths')) map.setPaintProperty('roads_paths', 'line-color', originalColors.paths);
      if (map.getLayer('roads_paths_casing')) map.setPaintProperty('roads_paths_casing', 'line-color', '#d0d0d0');

      setUseZoomColors(prev => { const next = {}; ROAD_CATEGORIES.forEach(id => { next[id] = false; }); return next; });
      setZoomColors(prev => {
        const next = {};
        ROAD_CATEGORIES.forEach(id => {
          const def = originalColors[id] || '#ccc';
          next[id] = ZOOM_KEYS.reduce((acc, k) => ({ ...acc, [k]: def }), {});
        });
        return next;
      });
      setZoomCasingColors(prev => {
        const next = {};
        ROAD_CATEGORIES.forEach(id => {
          next[id] = ZOOM_KEYS.reduce((acc, k) => ({ ...acc, [k]: '#999999' }), {});
        });
        return next;
      });

      // Reset railways
      if (map.getLayer('railways_main')) {
        map.setPaintProperty('railways_main', 'line-color', originalColors.railways);
      }
      if (map.getLayer('railways_minor')) {
        map.setPaintProperty('railways_minor', 'line-color', '#999');
      }

      // Reset aeroways
      if (map.getLayer('aeroways_runways_taxiways')) {
        map.setPaintProperty('aeroways_runways_taxiways', 'line-color', originalColors.aeroways);
      }
      if (map.getLayer('aeroways_apron_terminal')) {
        map.setPaintProperty('aeroways_apron_terminal', 'fill-color', '#e9e4eb');
      }

      // Reset boundaries
      if (map.getLayer('admin_l2_4_country')) {
        map.setPaintProperty('admin_l2_4_country', 'line-color', originalColors.boundaries);
      }
      if (map.getLayer('admin_l2_4_glow')) {
        map.setPaintProperty('admin_l2_4_glow', 'line-color', '#888888');
      }
      if (map.getLayer('admin_l2_4_states')) {
        map.setPaintProperty('admin_l2_4_states', 'line-color', '#b0b0b0');
      }
      if (map.getLayer('admin_l5_8')) {
        map.setPaintProperty('admin_l5_8', 'line-color', '#c0c0c0');
      }


      // Reset labels
      const labelLayers = [
        'places_country', 'places_country_fallback', 'places_country_state', 'places_country_state_fallback', 'places_city_town_combined',
        'places_village_suburb', 'water_lakes_labels',
        'landuse_vegetation_labels', 'landuse_agriculture_grass_labels',
        'buildings_labels', 'aeroways_apron_terminal_labels',
        'roads_mw_trunk_mid_labels', 'roads_primary_labels', 'roads_secondary_labels',
        'roads_tertiary_mid_labels', 'roads_residential_high_labels'
      ];
      
      labelLayers.forEach(layerId => {
        if (map.getLayer(layerId)) {
          // Reset to original colors based on layer type
          if (layerId.includes('water')) {
            map.setPaintProperty(layerId, 'text-color', '#2E7D8A');
          } else if (layerId.includes('vegetation') || layerId.includes('agriculture')) {
            map.setPaintProperty(layerId, 'text-color', layerId.includes('vegetation') ? '#2E7D32' : '#558B2F');
          } else if (layerId.includes('aeroway')) {
            map.setPaintProperty(layerId, 'text-color', '#5E35B1');
          } else if (layerId === 'places_city_town_combined') {
            map.setPaintProperty(layerId, 'text-color', '#2a2a2a');
          } else if (layerId.includes('places')) {
            map.setPaintProperty(layerId, 'text-color', '#555');
          } else if (layerId.includes('roads_mw') || layerId.includes('roads_primary')) {
            map.setPaintProperty(layerId, 'text-color', '#2C2C2C');
          } else if (layerId.includes('roads_tertiary')) {
            map.setPaintProperty(layerId, 'text-color', '#3C3C3C');
          } else {
            map.setPaintProperty(layerId, 'text-color', '#666');
          }
        }
      });
      saveMapStyleFromMapToStorage(map);
    } catch (error) {
      // Error resetting colors
    }
  };

  return (
    <div className="map-designer-overlay">
      <div className="map-designer-panel">
        <div className="map-designer-header">
          <h2>Design MapNow</h2>
          <button className="map-designer-close" onClick={onClose}>×</button>
        </div>

        <div className="map-designer-content">
          <div className="map-designer-tabs">
            {colorCategories.map(category => (
              <button
                key={category.id}
                className={`map-designer-tab ${activeTab === category.id ? 'active' : ''}`}
                onClick={() => setActiveTab(category.id)}
              >
                {category.label}
              </button>
            ))}
          </div>

          <div className="map-designer-editor">
            {colorCategories.map(category => (
              activeTab === category.id && (
                <div key={category.id} className="color-editor">
                  <div className="color-editor-header">
                    <h3>{category.label}</h3>
                    <p>{category.description}</p>
                  </div>

                  <div className="color-picker-container">
                    {/* Main color: "Road color" for roads, "Hex Color" for others */}
                    <div className="color-field">
                      {ROAD_CATEGORIES.includes(category.id) && <label>Road color</label>}
                      <div className="color-preview" style={{ backgroundColor: expandHex(colors[category.id]) || colors[category.id] || '#ccc' }}>
                        <span className="color-value">{colors[category.id] || '—'}</span>
                      </div>
                      <div className="color-input-group">
                        <label htmlFor={`color-${category.id}`}>{ROAD_CATEGORIES.includes(category.id) ? 'Hex:' : 'Hex Color:'}</label>
                        <input
                          type="text"
                          id={`color-${category.id}`}
                          value={colors[category.id] || ''}
                          onChange={(e) => handleColorChange(category.id, e.target.value)}
                          placeholder="#000000"
                          maxLength={7}
                        />
                        <input
                          type="color"
                          value={expandHex(colors[category.id]) || '#cccccc'}
                          onChange={(e) => handleColorChange(category.id, e.target.value)}
                          className="color-picker-native"
                        />
                      </div>
                    </div>

                    {/* Border (casing) - road types only */}
                    {ROAD_CATEGORIES.includes(category.id) && (
                      <div className="color-field">
                        <label>Border (casing) color <span className="optional">optional</span></label>
                        <div className="color-preview color-preview-small" style={{ backgroundColor: (colors[category.id + '_casing'] && expandHex(colors[category.id + '_casing'])) || adjustBrightness(colors[category.id] || '#ccc', -30) }}>
                          <span className="color-value">{colors[category.id + '_casing'] || 'Auto (darker)'}</span>
                        </div>
                        <div className="color-input-group">
                          <label htmlFor={`casing-${category.id}`}>Hex:</label>
                          <input
                            type="text"
                            id={`casing-${category.id}`}
                            value={colors[category.id + '_casing'] || ''}
                            onChange={(e) => handleCasingChange(category.id, e.target.value || null)}
                            placeholder="Auto"
                            maxLength={7}
                          />
                          <input
                            type="color"
                            value={expandHex(colors[category.id + '_casing']) || expandHex(adjustBrightness(colors[category.id] || '#ccc', -30)) || '#999999'}
                            onChange={(e) => handleCasingChange(category.id, e.target.value)}
                            className="color-picker-native"
                          />
                        </div>
                      </div>
                    )}

                    {/* Zoom-level colors - road types only (5 levels + border by zoom) */}
                    {ROAD_CATEGORIES.includes(category.id) && (
                      <div className="color-field zoom-colors">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={!!useZoomColors[category.id]}
                            onChange={(e) => handleUseZoomChange(category.id, e.target.checked)}
                          />
                          Use different colors by zoom level
                        </label>
                        {useZoomColors[category.id] && zoomColors[category.id] && (
                          <>
                            <div className="zoom-level-section">
                              <span className="zoom-section-label">Road color by zoom</span>
                              <div className="zoom-level-inputs">
                                {ZOOM_LABELS.map(({ key, label }) => (
                                  <div key={key} className="color-input-group">
                                    <label>{label}:</label>
                                    <input type="text" value={zoomColors[category.id][key] || ''} onChange={(e) => handleZoomColorChange(category.id, key, e.target.value)} placeholder="#hex" maxLength={7} />
                                    <input type="color" value={expandHex(zoomColors[category.id][key]) || '#cccccc'} onChange={(e) => handleZoomColorChange(category.id, key, e.target.value)} className="color-picker-native" />
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="zoom-level-section">
                              <span className="zoom-section-label">Border (casing) color by zoom</span>
                              <div className="zoom-level-inputs">
                                {ZOOM_LABELS.map(({ key, label }) => (
                                  <div key={key} className="color-input-group">
                                    <label>{label}:</label>
                                    <input type="text" value={zoomCasingColors[category.id]?.[key] || ''} onChange={(e) => handleZoomCasingColorChange(category.id, key, e.target.value)} placeholder="Auto" maxLength={7} />
                                    <input type="color" value={expandHex(zoomCasingColors[category.id]?.[key]) || expandHex(adjustBrightness(zoomColors[category.id]?.[key] || colors[category.id], -30)) || '#999999'} onChange={(e) => handleZoomCasingColorChange(category.id, key, e.target.value)} className="color-picker-native" />
                                  </div>
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>

        <div className="map-designer-actions">
          <button className="btn-reset" onClick={resetColors}>
            Reset to Default
          </button>
          <button className="btn-apply" onClick={() => applyColors(activeTab)} disabled={isApplying} title="Apply current tab only (e.g. only water) so other layers are not changed">
            {isApplying ? '✓ Applied!' : 'Apply Colors'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MapDesigner;
